<template>
    <div
        class="flex-none account-sidebar pointer-events-auto max-w-md transform transition ease-in-out duration-500 fixed right-0 top-28 bottom-0 overflow-hidden z-20 min-w-full lg:min-w-[28rem] shadow ring-1 ring-black ring-opacity-5"
        :class="{
            closed: !showAccountSidebar,
            open: showAccountSidebar,
        }"
    >
        <div
            class="flex h-full flex-col overflow-hidden py-10 shadow-xl relative"
            style="background-color: #f8fafc"
        >
            <div class="px-4 sm:px-6 lg:pt-10">
                <div class="flex justify-center mb-3">
                    <span v-if="activeAccount.card" class="block text-center">
                        <img
                            :src="`${cdnUrl}/${activeAccount.card.card_image?.path}/_small/${activeAccount.card.card_image?.file_name}`"
                            v-if="
                                activeAccount.card && activeAccount.card.image
                            "
                            class="mx-auto max-w-[10rem] lg:max-w-[60%]"
                        />
                        <img
                            :src="activeAccount.institution.medium_logo_url"
                            v-if="
                                activeAccount.card && !activeAccount.card.image
                            "
                            class="mx-auto max-w-[10rem] lg:max-w-[70%]"
                        />
                    </span>
                </div>
                <div class="mb-5 w-[75%] lg:w-full mx-auto">
                    <h2
                        class="text-lg font-medium text-gray-900 text-center"
                        v-if="activeAccount.name || activeAccount.card"
                    >
                        <span v-if="activeAccount.card">
                            {{ activeAccount.card.title }}
                        </span>
                        <span v-else>{{ activeAccount.name }}</span>
                        <small v-if="activeAccount.last_four">
                            ({{ activeAccount.last_four }})
                        </small>
                    </h2>
                    <div
                        class="ml-3 flex h-7 items-center absolute top-3 right-4"
                    >
                        <button
                            type="button"
                            class="btn-icon"
                            @click="setAccountSidebar(false)"
                            id="hs-wallet-close-sidebar"
                        >
                            <span class="sr-only">Close panel</span>
                            <svg
                                class="h-6 w-6"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="2"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            <div class="relative flex-1 px-4 sm:px-6">
                <div class="absolute inset-0 px-4 sm:px-6">
                    <div class="h-full overflow-auto" aria-hidden="true">
                        <div class="mt-8">
                            <h4
                                class="text-base font-semibold leading-6 text-slate-800 mb-2"
                            >
                                Details
                            </h4>

                            <dl class="mb-6">
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                    v-if="activeAccount.card"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Card Type
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-medium leading-5 text-slate-800"
                                    >
                                        {{
                                            activeAccount.card.type ===
                                            'personal'
                                                ? 'Personal Card'
                                                : 'Business Card'
                                        }}
                                    </dd>
                                </div>
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Status
                                    </dt>
                                    <dd
                                        class="text-right sm:mt-0 font-semibold"
                                    >
                                        <span
                                            v-if="
                                                activeAccount.card_status ===
                                                'open'
                                            "
                                        >
                                            <span
                                                class="inline-flex items-center px-3 py-0.5 font-medium rounded-full text-sm bg-green-100 text-green-700 capitalize"
                                            >
                                                Open
                                            </span>
                                            <span
                                                class="text-xs font-normal ml-2"
                                                v-if="activeAccount.date_opened"
                                            >
                                                ({{
                                                    activeAccount.date_opened
                                                }})
                                            </span>
                                        </span>
                                        <span
                                            v-if="
                                                activeAccount.card_status ===
                                                'closed'
                                            "
                                        >
                                            <span
                                                class="inline-flex items-center rounded-full bg-red-100 px-3 py-0.5 text-sm font-medium text-red-700 mb-1"
                                                v-if="
                                                    activeAccount.card_status ===
                                                    'closed'
                                                "
                                            >
                                                Closed
                                            </span>
                                            <span
                                                class="text-xs font-normal ml-2"
                                                v-if="activeAccount.date_closed"
                                            >
                                                ({{
                                                    activeAccount.date_closed
                                                }})
                                            </span>
                                        </span>
                                    </dd>
                                </div>
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                    v-if="activeAccount.account_type"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Account Type
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-medium leading-5 text-slate-800"
                                    >
                                        {{
                                            activeAccount.account_type ===
                                            'authorized_user'
                                                ? 'Authorized User'
                                                : 'Primary Cardholder'
                                        }}

                                        <span
                                            class="inline-flex items-center justify-center rounded-full bg-gray-100 !text-gray-600 text-xs w-6 h-6 ml-1"
                                            v-if="
                                                activeAccount.account_type ===
                                                'authorized_user'
                                            "
                                        >
                                            <i
                                                class="fas fa-user-friends !text-gray-600 fa-sm"
                                            ></i>
                                        </span>
                                    </dd>
                                </div>
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Annual Fee
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-medium leading-5 text-slate-800"
                                        v-if="
                                            !activeAccount.user_managed_annual_fee
                                        "
                                    >
                                        <span
                                            class="font-semibold text-sm leading-none"
                                            v-if="
                                                activeAccount.card &&
                                                activeAccount.card
                                                    .initial_annual_fee &&
                                                activeAccount.initial_annual_fee_active
                                            "
                                        >
                                            <span
                                                class="text-red-400 line-through text-sm block"
                                            >
                                                <span
                                                    class="text-gray-500 text-sm"
                                                >
                                                    ${{
                                                        activeAccount.card
                                                            .annual_fee
                                                    }}
                                                </span>
                                            </span>
                                            <span class="block font-normal">
                                                <span
                                                    class="text-sm font-semibold"
                                                >
                                                    ${{
                                                        activeAccount.card
                                                            .initial_annual_fee
                                                    }}
                                                </span>
                                                for your first year, then
                                                <span class="font-semibold">
                                                    ${{
                                                        activeAccount.card
                                                            .annual_fee
                                                    }}
                                                </span>
                                            </span>
                                        </span>

                                        <span
                                            class="font-semibold text-sm leading-none"
                                            v-if="
                                                activeAccount.card &&
                                                activeAccount.card.annual_fee &&
                                                !activeAccount.initial_annual_fee_active
                                            "
                                        >
                                            ${{ activeAccount.card.annual_fee }}
                                        </span>
                                        <span
                                            class="inline-block rounded-full bg-gray-400 text-white text-center relative annual-fee-label-trigger"
                                            style="width: 22px; height: 22px"
                                            v-if="
                                                activeAccount.card &&
                                                activeAccount.card
                                                    .annual_fee_label
                                            "
                                        >
                                            <span
                                                class="annual-fee-label font-semibold text-sm leading-none"
                                            >
                                                {{
                                                    activeAccount.card
                                                        .annual_fee_label
                                                }}
                                            </span>
                                            <i class="fas fa-exclamation"></i>
                                        </span>
                                        <span
                                            class="text-xs font-normal ml-2 block"
                                            v-if="
                                                activeAccount.card &&
                                                activeAccount.date_opened &&
                                                activeAccount.annual_fee_due_date &&
                                                activeAccount.card.annual_fee >
                                                    0
                                            "
                                        >
                                            (Due:
                                            {{
                                                activeAccount
                                                    .annual_fee_due_date.date
                                            }})
                                        </span>
                                    </dd>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-bold"
                                        v-if="
                                            activeAccount.user_managed_annual_fee
                                        "
                                    >
                                        <span
                                            class="font-semibold text-sm leading-none"
                                            :class="{
                                                '!text-gray-400 line-through':
                                                    activeAccount.card_status ===
                                                    'closed',
                                            }"
                                        >
                                            ${{ activeAccount.annual_fee }}
                                        </span>
                                        <span
                                            class="text-xs font-normal ml-2 block"
                                            v-if="
                                                activeAccount.card &&
                                                activeAccount.date_opened &&
                                                activeAccount.annual_fee > 0 &&
                                                activeAccount.card_status !==
                                                    'closed'
                                            "
                                        >
                                            (Due:
                                            {{
                                                activeAccount
                                                    .annual_fee_due_date.date
                                            }})
                                        </span>
                                    </dd>
                                </div>
                            </dl>

                            <h4
                                class="text-base font-semibold leading-6 text-slate-800 mt-8 mb-2"
                                v-if="!activeAccount.product_changed"
                            >
                                Welcome Bonus
                            </h4>

                            <dl
                                class="mb-6"
                                v-if="!activeAccount.product_changed"
                            >
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Bonus Amount
                                    </dt>
                                    <dd
                                        class="text-xs text-right sm:mt-0 font-semibold"
                                    >
                                        <span
                                            v-if="activeAccount.bonus_amount"
                                            class="text-sm font-semibold"
                                        >
                                            {{
                                                $filters.formatThousand(
                                                    activeAccount.bonus_amount
                                                )
                                            }}

                                            <span
                                                class="font-normal text-xs"
                                                v-if="
                                                    activeAccount &&
                                                    activeAccount.card.program
                                                "
                                            >
                                                <br />
                                                {{
                                                    activeAccount.card.program
                                                        .name
                                                }}
                                            </span>
                                        </span>

                                        <span
                                            class="text-xs font-normal"
                                            v-else
                                        >
                                            (Add the original bonus amount
                                            associated with this card.)
                                        </span>
                                    </dd>
                                </div>
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Bonus Received
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-semibold"
                                    >
                                        <span
                                            v-if="
                                                activeAccount.bonus_received &&
                                                activeAccount.date_received
                                            "
                                            class="text-sm font-semibold"
                                        >
                                            <span
                                                class="inline-block rounded-full bg-green-400 w-2 h-2 mr-1"
                                            ></span>
                                            Yes
                                            <span class="text-xs font-normal">
                                                ({{
                                                    activeAccount.date_received
                                                }})
                                            </span>
                                        </span>
                                        <span
                                            v-else
                                            class="text-sm font-semibold"
                                        >
                                            <span
                                                class="inline-block rounded-full bg-red-400 w-2 h-2 mr-1"
                                            ></span>
                                            No
                                        </span>
                                    </dd>
                                </div>
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Minimum Spend Requirement
                                    </dt>
                                    <dd
                                        class="text-xs text-right sm:mt-0 font-semibold"
                                    >
                                        <span
                                            v-if="
                                                activeAccount.minimum_spend_amount
                                            "
                                            class="text-sm font-semibold"
                                        >
                                            ${{
                                                $filters.formatThousand(
                                                    activeAccount.minimum_spend_amount
                                                )
                                            }}
                                        </span>

                                        <span
                                            class="text-xs font-normal block"
                                            v-if="
                                                activeAccount.minimum_spend_deadline &&
                                                !activeAccount.bonus_received &&
                                                !activeAccount.date_received
                                            "
                                        >
                                            (Must meet by:
                                            {{
                                                activeAccount.minimum_spend_deadline
                                            }})
                                        </span>
                                        <span
                                            class="text-xs font-normal block"
                                            v-else
                                        >
                                            <span
                                                v-if="
                                                    !activeAccount.bonus_received &&
                                                    !activeAccount.date_received
                                                "
                                            >
                                                (Add your minimum spend
                                                requirement details to help
                                                ensure you meet your deadline
                                                and can track it historically.)
                                            </span>
                                        </span>
                                    </dd>
                                </div>
                            </dl>

                            <h4
                                class="text-base font-semibold leading-6 text-slate-800 mt-8 mb-2"
                                v-if="activeAccount.product_changed"
                            >
                                Product Change Details
                            </h4>

                            <dl
                                class="mb-6"
                                v-if="activeAccount.product_changed"
                            >
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Original Card
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-semibold"
                                    >
                                        {{
                                            activeAccount.original_card
                                                ? activeAccount.original_card
                                                      .title
                                                : ''
                                        }}
                                    </dd>
                                </div>
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Original Card Open Date
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-semibold"
                                    >
                                        <span
                                            v-if="
                                                activeAccount.product_changed_date_opened
                                            "
                                        >
                                            {{
                                                activeAccount.product_changed_date_opened
                                            }}
                                        </span>
                                        <span
                                            class="text-xs font-normal"
                                            v-else
                                        >
                                            (Add the original bonus amount
                                            associated with this card.)
                                        </span>
                                    </dd>
                                </div>

                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Original Bonus Amount
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-semibold"
                                    >
                                        <span
                                            v-if="
                                                activeAccount.product_changed_bonus_amount
                                            "
                                        >
                                            {{
                                                $filters.formatThousand(
                                                    activeAccount.product_changed_bonus_amount
                                                )
                                            }}
                                            <span
                                                class="font-normal text-xs"
                                                v-if="
                                                    activeAccount.original_card &&
                                                    activeAccount.original_card
                                                        .program
                                                "
                                            >
                                                <br />
                                                {{
                                                    activeAccount.original_card
                                                        .program.name
                                                }}
                                            </span>
                                            <span
                                                class="text-xs font-normal block"
                                                v-if="
                                                    activeAccount.product_changed_date_opened
                                                "
                                            >
                                                (Bonus Originally Met
                                                {{
                                                    activeAccount.original_card_bonus_date
                                                }})
                                            </span>
                                        </span>
                                        <span
                                            class="text-xs font-normal"
                                            v-else
                                        >
                                            (Add the original bonus amount
                                            associated with this card.)
                                        </span>
                                    </dd>
                                </div>
                                <div
                                    class="py-4 sm:grid sm:grid-cols-3 sm:gap-1 border-b border-indigo-100"
                                    v-if="
                                        activeAccount.bonus_received &&
                                        activeAccount.date_received
                                    "
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Original Bonus Received
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 sm:col-span-2 font-semibold"
                                    >
                                        <span
                                            v-if="activeAccount.bonus_received"
                                        >
                                            <span
                                                class="inline-block rounded-full bg-green-400 w-2 h-2 mr-1"
                                            ></span>
                                            Yes
                                            <span class="text-xs font-normal">
                                                ({{
                                                    activeAccount.date_received
                                                }})
                                            </span>
                                        </span>
                                        <span v-else>
                                            <span
                                                class="inline-block rounded-full bg-red-400 w-2 h-2 mr-1"
                                            ></span>
                                            No
                                            <span class="text-xs font-normal">
                                                ({{
                                                    activeAccount.date_received
                                                }})
                                            </span>
                                        </span>
                                    </dd>
                                </div>
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Original Minimum Spend Requirement
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-semibold"
                                    >
                                        <span
                                            v-if="
                                                activeAccount.product_changed_min_spend
                                            "
                                        >
                                            ${{
                                                $filters.formatThousand(
                                                    activeAccount.product_changed_min_spend
                                                )
                                            }}
                                        </span>

                                        <span
                                            class="text-xs font-normal"
                                            v-else
                                        >
                                            (Please add your original minimum
                                            spend requirement.)
                                        </span>
                                    </dd>
                                </div>
                                <div
                                    class="py-3 lg:py-4 grid grid-cols-2 gap-1 border-b border-indigo-100"
                                >
                                    <dt
                                        class="text-sm font-medium leading-5 text-slate-500"
                                    >
                                        Date Product Changed
                                    </dt>
                                    <dd
                                        class="text-sm text-right sm:mt-0 font-semibold"
                                    >
                                        <span
                                            v-if="
                                                activeAccount.product_changed_date
                                            "
                                        >
                                            {{
                                                activeAccount.product_changed_date
                                            }}
                                        </span>

                                        <span
                                            class="text-xs font-normal"
                                            v-else
                                        >
                                            (Please add the date you product
                                            changed this card.)
                                        </span>
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <!-- Bonus Spending Categories Section - Only show if RewardsCC data exists -->
                        <div
                            v-if="
                                hasFeatureFlag(
                                    'spending-categories-card-benefits'
                                ) && displayedBonusCategories.length > 0
                            "
                        >
                            <h4
                                class="text-base font-semibold leading-6 text-slate-800 mt-8 mb-2"
                            >
                                Bonus Spending Categories
                            </h4>
                            <dl class="mb-6">
                                <div
                                    v-for="(
                                        category, index
                                    ) in displayedBonusCategories"
                                    :key="index"
                                    class="py-3"
                                    :class="{
                                        'fade-bottom':
                                            !showAllBonusCategories &&
                                            index === 4 &&
                                            cardRewardsData
                                                .spend_bonus_categories.length >
                                                5,
                                    }"
                                >
                                    <div class="flex items-start">
                                        <div
                                            class="flex justify-center items-center w-9 py-0.5 px-0 rounded bg-slate-200 text-center font-medium text-sm leading-5 mr-3 flex-shrink-0"
                                        >
                                            {{ category.earnMultiplier
                                            }}{{
                                                category.rate_type ===
                                                'percentage'
                                                    ? '%'
                                                    : 'x'
                                            }}
                                        </div>
                                        <div class="flex-1">
                                            <div
                                                class="text-sm font-normal leading-5 text-gray-700"
                                            >
                                                {{
                                                    formatBonusDescription(
                                                        category
                                                    )
                                                }}
                                            </div>
                                            <div
                                                v-if="
                                                    category.isSpendLimit &&
                                                    category.spendLimit > 0
                                                "
                                                class="text-xs text-gray-500 mt-0.5"
                                            >
                                                <span>
                                                    Up to ${{
                                                        this.formatNumber(
                                                            category.spendLimit
                                                        )
                                                    }}
                                                    <span
                                                        v-if="
                                                            category.spendLimitResetPeriod
                                                        "
                                                    >
                                                        per
                                                        {{
                                                            category.spendLimitResetPeriod.toLowerCase()
                                                        }}
                                                    </span>
                                                </span>
                                            </div>
                                            <div
                                                v-if="
                                                    category.isDateLimit &&
                                                    category.limitBeginDate &&
                                                    category.limitEndDate
                                                "
                                                class="text-xs text-gray-500 mt-0.5"
                                            >
                                                Valid from
                                                {{
                                                    formatDate(
                                                        category.limitBeginDate
                                                    )
                                                }}
                                                to
                                                {{
                                                    formatDate(
                                                        category.limitEndDate
                                                    )
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        cardRewardsData.spend_bonus_categories
                                            .length > 5
                                    "
                                    class="mt-2 text-left"
                                >
                                    <button
                                        @click="toggleBonusCategories"
                                        class="btn-base btn-transparent"
                                        id="hs-wallet-show-bonus-categories"
                                    >
                                        {{
                                            showAllBonusCategories
                                                ? 'Show less'
                                                : 'Show all'
                                        }}
                                    </button>
                                </div>
                            </dl>
                        </div>

                        <!-- Card Benefits Section - Only show if RewardsCC data exists -->
                        <div
                            v-if="
                                hasFeatureFlag(
                                    'spending-categories-card-benefits'
                                ) && displayedCardBenefits.length > 0
                            "
                        >
                            <h4
                                class="text-base font-semibold leading-6 text-slate-800 mb-2 mt-8"
                            >
                                Card Benefits
                            </h4>
                            <dl class="mb-6">
                                <div
                                    v-for="(
                                        benefit, index
                                    ) in displayedCardBenefits"
                                    :key="index"
                                    class="py-3"
                                    :class="{
                                        'fade-bottom':
                                            !showAllCardBenefits &&
                                            index === 4 &&
                                            cardRewardsData.benefits.length > 5,
                                    }"
                                >
                                    <div class="flex items-start">
                                        <div class="flex-shrink-0 mr-3">
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M17.992 2.66084C18.3635 2.93259 18.4443 3.454 18.1725 3.82544L8.41764 17.1588C8.28376 17.3418 8.08108 17.4623 7.85636 17.4926C7.63164 17.5229 7.40429 17.4603 7.22675 17.3192L1.98165 13.1526C1.62128 12.8663 1.56121 12.3421 1.84749 11.9817C2.13376 11.6213 2.65797 11.5613 3.01834 11.8476L7.58451 15.4749L16.8274 2.84134C17.0992 2.4699 17.6206 2.38908 17.992 2.66084Z"
                                                    fill="#1E293B"
                                                />
                                            </svg>
                                        </div>
                                        <div class="flex-1">
                                            <dt
                                                class="text-sm text-slate-800 font-medium leading-5 mb-1"
                                            >
                                                {{
                                                    benefit.benefitTitle ||
                                                    benefit.benefit_title
                                                }}
                                            </dt>
                                            <dd
                                                class="text-sm text-slate-500 font-normal leading-5"
                                                v-if="
                                                    benefit.benefitDesc ||
                                                    benefit.benefit_desc
                                                "
                                            >
                                                {{
                                                    benefit.benefitDesc ||
                                                    benefit.benefit_desc
                                                }}
                                            </dd>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-if="cardRewardsData.benefits.length > 5"
                                    class="mt-2 text-left"
                                >
                                    <button
                                        @click="toggleCardBenefits"
                                        class="btn-base btn-transparent"
                                        id="hs-wallet-show-benefits"
                                    >
                                        {{
                                            showAllCardBenefits
                                                ? 'Show less'
                                                : 'Show all'
                                        }}
                                    </button>
                                </div>
                            </dl>
                        </div>
                        <!-- Edit Card -->
                        <div>
                            <div class="flex justify-center mt-8 mb-12">
                                <button
                                    class="btn-base btn-white"
                                    @click.prevent="openEditCardModal"
                                    id="hs-wallet-edit-card"
                                >
                                    <Icon icon="edit" class="w-5 h-5" />
                                    <span>Edit Card</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- /End replace -->
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useWalletStore } from '@/store/wallet';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    props: {
        user: { type: Object },
        isWalletOwner: { type: Boolean, default: false },
        isDenying: { type: Boolean, default: false },
    },
    components: {
        Icon,
    },
    data() {
        return {
            localApiUrl: 'api',
            showAllBonusCategories: false,
            showAllCardBenefits: false,
            rewardsccData: null,
            loadingRewardscc: false,
            rewardsccError: null,
        };
    },

    computed: {
        ...mapState(useWalletStore, [
            'activeAccount',
            'showAccountSidebar',
            'wallet',
            'features',
        ]),
        cdnUrl() {
            return window.cdnUrl;
        },

        hasFeatureFlag() {
            return (flagName) => {
                if (
                    this.wallet &&
                    this.wallet.features &&
                    typeof this.wallet.features[flagName] === 'boolean'
                ) {
                    return this.wallet.features[flagName];
                }

                if (
                    this.features &&
                    typeof this.features[flagName] === 'boolean'
                ) {
                    return this.features[flagName];
                }

                return false;
            };
        },

        cardRewardsData() {
            // If feature flag is not enabled, don't show anything
            if (!this.hasFeatureFlag('spending-categories-card-benefits')) {
                return {
                    spend_bonus_categories: [],
                    benefits: [],
                };
            }

            // Try to use the data from the rewardsccData property
            if (this.rewardsccData) {
                return this.processRewardsData(this.rewardsccData);
            }

            // Next, try to use data from the card object
            if (
                this.activeAccount &&
                this.activeAccount.card &&
                this.activeAccount.card.rewardscc_data
            ) {
                return this.processRewardsData(
                    this.activeAccount.card.rewardscc_data
                );
            }

            // If no data available, return empty structure
            return {
                spend_bonus_categories: [],
                benefits: [],
            };
        },

        displayedBonusCategories() {
            if (
                !this.cardRewardsData ||
                !this.cardRewardsData.spend_bonus_categories
            ) {
                return [];
            }

            return this.showAllBonusCategories
                ? this.cardRewardsData.spend_bonus_categories
                : this.cardRewardsData.spend_bonus_categories.slice(0, 5);
        },

        displayedCardBenefits() {
            if (!this.cardRewardsData || !this.cardRewardsData.benefits) {
                return [];
            }

            return this.showAllCardBenefits
                ? this.cardRewardsData.benefits
                : this.cardRewardsData.benefits.slice(0, 5);
        },
    },

    watch: {
        activeAccount: {
            immediate: true,
            handler(newAccount) {
                this.showAllBonusCategories = false;
                this.showAllCardBenefits = false;

                if (
                    newAccount &&
                    newAccount.card &&
                    newAccount.card.rewardscc_data
                ) {
                    this.rewardsccData = newAccount.card.rewardscc_data;
                } else {
                    this.rewardsccData = null;
                }
            },
        },
    },

    methods: {
        ...mapActions(useWalletStore, [
            'setAccountSidebar',
            'setSelectedCard',
            'setShowEditCardModal',
            'setSelectedUser',
            'setShowCopyCardModal',
            'closeAccountSidebar',
        ]),

        formatBonusDescription(category) {
            if (category.spendBonusDesc) {
                return category.spendBonusDesc;
            }

            let desc = '';

            desc = `Earn ${category.earnMultiplier}${category.rate_type === 'percentage' ? '%' : 'x'} on ${category.spendBonusCategoryName}`;

            if (category.isSpendLimit && category.spendLimit > 0) {
                desc += ` on up to $${this.formatNumber(category.spendLimit)}`;

                if (category.spendLimitResetPeriod) {
                    desc += ` per ${category.spendLimitResetPeriod.toLowerCase()}`;
                }
            }

            return desc;
        },

        formatNumber(number) {
            return new Intl.NumberFormat('en-US').format(number);
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        },

        openEditCardModal() {
            this.setSelectedCard(this.activeAccount);
            this.setSelectedUser(this.user);
            this.setShowEditCardModal(true);
            this.closeAccountSidebar();
        },
        toggleBonusCategories() {
            this.showAllBonusCategories = !this.showAllBonusCategories;
        },
        toggleCardBenefits() {
            this.showAllCardBenefits = !this.showAllCardBenefits;
        },
        processRewardsData(data) {
            if (!data) return { spend_bonus_categories: [], benefits: [] };

            // Create a copy to avoid mutating the original
            const processed = { ...data };

            // Process benefits_config if available
            if (data.benefits_config) {
                // Parse benefits_config if it's a string
                const benefitsConfig =
                    typeof data.benefits_config === 'string'
                        ? JSON.parse(data.benefits_config || '[]')
                        : data.benefits_config || [];

                // If we have benefits and config data, apply ordering and filtering
                if (
                    Array.isArray(data.benefits) &&
                    data.benefits.length > 0 &&
                    benefitsConfig.length > 0
                ) {
                    // Create a map of benefits by title for quick lookup
                    const benefitsMap = new Map();
                    data.benefits.forEach((benefit) => {
                        const key =
                            benefit.benefit_title || benefit.benefitTitle || '';
                        if (key) {
                            benefitsMap.set(key, benefit);
                        }
                    });

                    // Apply ordering and filtering from config
                    const orderedBenefits = benefitsConfig
                        .filter((config) => !config.hidden) // Filter out hidden benefits
                        .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order
                        .map((config) => {
                            const benefitTitle =
                                config.benefitTitle || config.benefit_title;
                            return benefitsMap.get(benefitTitle);
                        })
                        .filter((benefit) => !!benefit); // Remove any undefined benefits

                    // Replace the benefits array with ordered and filtered benefits
                    processed.benefits = orderedBenefits;
                }
            }

            return processed;
        },
    },
};
</script>
<style lang="scss" scoped>
.account-sidebar {
    transform: translateX(100%);
    &.open {
        transform: translateX(0);
    }
}

.fade-bottom {
    mask-image: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 1) 40%,
        rgba(0, 0, 0, 0) 90%
    );
    -webkit-mask-image: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 1) 40%,
        rgba(0, 0, 0, 0) 90%
    );
    border-bottom-color: transparent !important;
    max-height: 80px;
    overflow: hidden;
}
</style>
